import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Fix for "process is not defined" error
if (typeof window !== 'undefined' && !window.process) {
    window.process = { env: { NODE_ENV: 'production' } };
}

import SVGA from 'svgaplayerweb';
import './SVGAPlayer.css';

class SVGAPlayer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            player: null,
            isPlaying: false,
            isLoading: false,
            error: null
        };
        this.canvasRef = React.createRef();
        this.player = null;
        this.parser = null;
    }

    componentDidMount() {
        if (this.props.animatedUrl) {
            this.loadAnimation(this.props.animatedUrl);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.animatedUrl !== this.props.animatedUrl && this.props.animatedUrl) {
            this.loadAnimation(this.props.animatedUrl);
        }
    }

    componentWillUnmount() {
        this.cleanup();
    }

    cleanup = () => {
        if (this.player) {
            this.player.clear();
            this.player = null;
        }
        if (this.parser) {
            this.parser = null;
        }
    }

    loadAnimation = (url) => {
        if (!url) return;

        this.setState({ isLoading: true, error: null });
        
        try {
            // Check if SVGA library is available
            if (!SVGA || !SVGA.Player || !SVGA.Parser) {
                throw new Error('SVGA library not available');
            }
            
            // Cleanup previous player
            this.cleanup();

            // Create new player and parser
            this.player = new SVGA.Player(this.canvasRef.current);
            this.parser = new SVGA.Parser(this.canvasRef.current);

            // Load SVGA với callback như cũ
            console.log('Loading SVGA from URL:', url);
            this.parser.load(url, (videoItem) => {
                console.log('SVGA loaded successfully:', videoItem);
                if (videoItem) {
                    this.player.setVideoItem(videoItem);
                    this.setState({ 
                        isLoading: false, 
                        isPlaying: false,
                        error: null 
                    });
                    
                    if (this.props.autoPlay) {
                        this.startAnimation();
                    }
                } else {
                    console.log('SVGA videoItem is null');
                    this.setState({ 
                        isLoading: false, 
                        error: 'Không thể load animation' 
                    });
                }
            }, (error) => {
                console.error('SVGA loading error:', error);
                
                let errorMessage = 'Lỗi khi tải SVGA animation.';
                
                // Check for CORS error
                if (error && error.message && error.message.includes('CORS')) {
                    errorMessage = 'Lỗi CORS: Không thể truy cập file từ domain này.';
                } else if (error && error.type === 'error') {
                    errorMessage = 'Lỗi network: Không thể tải file.';
                }
                
                this.setState({ 
                    isLoading: false, 
                    error: errorMessage
                });
            });

        } catch (error) {
            console.error('SVGA error:', error);
            this.setState({ 
                isLoading: false, 
                error: 'Lỗi khởi tạo SVGA player. Có thể do CORS hoặc network issue.' 
            });
        }
    }



    startAnimation = () => {
        if (this.player) {
            this.player.startAnimation();
            this.setState({ isPlaying: true });
        }
    }

    stopAnimation = () => {
        if (this.player) {
            this.player.stopAnimation();
            this.setState({ isPlaying: false });
        }
    }

    pauseAnimation = () => {
        if (this.player) {
            this.player.pauseAnimation();
            this.setState({ isPlaying: false });
        }
    }

    handleCanvasClick = () => {
        if (this.props.onClick) {
            this.props.onClick({
                animatedUrl: this.props.animatedUrl,
                giftName: this.props.giftName,
                isPlaying: this.state.isPlaying
            });
        }
    }

    handlePlayPause = () => {
        if (this.state.isPlaying) {
            this.pauseAnimation();
        } else {
            this.startAnimation();
        }
    }

    render() {
        const { 
            width = '100%', 
            height = '100%', 
            className = '',
            showControls = false,
            giftName = 'Animation'
        } = this.props;

        const { isLoading, error, isPlaying } = this.state;

        return (
            <div className={`svga-player-container ${className}`}>
                <div 
                    ref={this.canvasRef}
                    className="svga-canvas"
                    style={{
                        width: width,
                        height: height,
                        cursor: this.props.onClick ? 'pointer' : 'default'
                    }}
                    onClick={this.handleCanvasClick}
                    title={giftName}
                />
                
                {isLoading && (
                    <div className="svga-loading">
                        <div className="loading-spinner"></div>
                        <span>Đang tải...</span>
                    </div>
                )}

                {error && (
                    <div className="svga-error">
                        <span>⚠️ {error}</span>
                        <div style={{ marginTop: '8px', fontSize: '10px', textAlign: 'center' }}>
                            <div style={{ marginBottom: '4px' }}>
                                <a href={this.props.animatedUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>
                                    🔗 Mở trong tab mới
                                </a>
                            </div>
                            <div style={{ fontSize: '9px', opacity: 0.8 }}>
                                Hoặc sử dụng SVGA Viewer:
                            </div>
                            <div style={{ marginTop: '2px' }}>
                                <a 
                                    href={`https://svga.io/svga-preview.html?url=${encodeURIComponent(this.props.animatedUrl)}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    style={{ color: '#fff', textDecoration: 'underline' }}
                                >
                                    🎭 SVGA Viewer
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {showControls && !isLoading && !error && (
                    <div className="svga-controls">
                        <button 
                            className="svga-control-btn"
                            onClick={this.handlePlayPause}
                            title={isPlaying ? 'Tạm dừng' : 'Phát'}
                        >
                            {isPlaying ? '⏸️' : '▶️'}
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

SVGAPlayer.propTypes = {
    animatedUrl: PropTypes.string,
    giftName: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    showControls: PropTypes.bool,
    autoPlay: PropTypes.bool,
    onClick: PropTypes.func
};

SVGAPlayer.defaultProps = {
    showControls: false,
    autoPlay: false
};

export default SVGAPlayer; 